import React, { useMemo, useState } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Grid,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip,
  Button,
  Stepper,
  Step,
  StepLabel,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  TableContainer,
  Divider,
} from '@mui/material';
import { TrendingUp, Refresh } from '@mui/icons-material';

const ProgressTracking = () => {
  // Filters / header
  const [header, setHeader] = useState({
    projectName: 'GreenVille - Rooftop A1',
    projectRef: 'PRJ-10231',
    filterStage: '',
  });

  // Sample workflow stages (should map to Green Energy Workflow master)
  const stages = useMemo(() => ([
    { key: 'lead', label: 'Lead Generation' },
    { key: 'survey', label: 'Site Survey' },
    { key: 'quote', label: 'Quotation' },
    { key: 'order', label: 'Order Confirmed' },
    { key: 'plan', label: 'Installation Planning' },
    { key: 'install', label: 'Installation' },
    { key: 'qc', label: 'Quality Check' },
    { key: 'commission', label: 'Commissioning' },
    { key: 'handover', label: 'Handover' },
    { key: 'amc', label: 'AMC / Maintenance' },
  ]), []);

  // Sample current stage and stage statuses
  const [currentStageIndex, setCurrentStageIndex] = useState(6); // e.g., QC in progress

  // Milestones table
  const [milestones] = useState([
    { id: 1, stage: 'Site Survey', due: '2025-10-28', done: '2025-10-28', owner: 'Amit Kumar', status: 'Completed' },
    { id: 2, stage: 'Quotation', due: '2025-10-29', done: '2025-10-29', owner: 'Riya Verma', status: 'Completed' },
    { id: 3, stage: 'Order Confirmed', due: '2025-10-30', done: '2025-10-30', owner: 'Admin', status: 'Completed' },
    { id: 4, stage: 'Installation Planning', due: '2025-10-31', done: '-', owner: 'Rahul Singh', status: 'In Progress' },
    { id: 5, stage: 'Installation', due: '2025-11-02', done: '-', owner: 'Install Team', status: 'Pending' },
  ]);

  const statusColor = (status) => {
    if (status === 'Completed') return 'success';
    if (status === 'In Progress') return 'warning';
    return 'default';
  };

  return (
    <Box sx={{ p: 3, backgroundColor: '#f5f5f5', minHeight: '100vh' }}>
      {/* Header */}
      <Box sx={{ mb: 3 }}>
        <Box display="flex" justifyContent="space-between" alignItems="center" flexWrap="wrap" gap={2}>
          <Box>
            <Typography variant="h4" sx={{ fontWeight: 700, color: '#000', mb: 0.5 }}>
              Project Progress Tracking
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Track workflow stages, milestones, and progress for green energy installations
            </Typography>
          </Box>
          <Button variant="outlined" size="small" startIcon={<Refresh />}>Refresh</Button>
        </Box>
      </Box>

      {/* Project Summary & Filters */}
      <Card sx={{ mb: 2 }}>
        <CardContent>
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} md={4}>
              <TextField size="small" fullWidth label="Project Name" value={header.projectName} onChange={(e) => setHeader(prev => ({ ...prev, projectName: e.target.value }))} />
            </Grid>
            <Grid item xs={12} md={4}>
              <TextField size="small" fullWidth label="Project Reference" value={header.projectRef} onChange={(e) => setHeader(prev => ({ ...prev, projectRef: e.target.value }))} />
            </Grid>
            <Grid item xs={12} md={4}>
              <FormControl size="small" fullWidth>
                <InputLabel>Filter by Stage</InputLabel>
                <Select value={header.filterStage} label="Filter by Stage" onChange={(e) => setHeader(prev => ({ ...prev, filterStage: e.target.value }))}>
                  <MenuItem value="">All Stages</MenuItem>
                  {stages.map(s => <MenuItem key={s.key} value={s.key}>{s.label}</MenuItem>)}
                </Select>
              </FormControl>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {/* Workflow Stepper */}
      <Card sx={{ mb: 2 }}>
        <CardContent>
          <Box display="flex" alignItems="center" gap={1} mb={2}>
            <TrendingUp color="primary" />
            <Typography variant="h6" sx={{ fontWeight: 700 }}>Workflow</Typography>
          </Box>
          <Stepper activeStep={currentStageIndex} alternativeLabel>
            {stages.map((s) => (
              <Step key={s.key}>
                <StepLabel>{s.label}</StepLabel>
              </Step>
            ))}
          </Stepper>
          <Box mt={2} display="flex" gap={1} flexWrap="wrap">
            <Chip label={`Current: ${stages[currentStageIndex]?.label || '-'}`} color="primary" />
            <Chip label={`Completed: ${currentStageIndex}`} color="success" variant="outlined" />
          </Box>
        </CardContent>
      </Card>

      {/* Milestones */}
      <Card sx={{ mb: 2 }}>
        <CardContent>
          <Typography variant="h6" sx={{ fontWeight: 700, mb: 1 }}>Milestones</Typography>
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow sx={{ backgroundColor: '#536DFE' }}>
                  <TableCell sx={{ color: '#fff', fontWeight: 700 }}>Stage</TableCell>
                  <TableCell sx={{ color: '#fff', fontWeight: 700 }}>Due Date</TableCell>
                  <TableCell sx={{ color: '#fff', fontWeight: 700 }}>Completed</TableCell>
                  <TableCell sx={{ color: '#fff', fontWeight: 700 }}>Owner</TableCell>
                  <TableCell sx={{ color: '#fff', fontWeight: 700 }}>Status</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {milestones
                  .filter(m => !header.filterStage || m.stage.toLowerCase().includes(header.filterStage))
                  .map((m) => (
                  <TableRow key={m.id} hover>
                    <TableCell sx={{ fontWeight: 600 }}>{m.stage}</TableCell>
                    <TableCell>{m.due}</TableCell>
                    <TableCell>{m.done}</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>{m.owner}</TableCell>
                    <TableCell>
                      <Chip label={m.status} color={statusColor(m.status)} size="small" sx={{ fontWeight: 600 }} />
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </CardContent>
      </Card>

      {/* Activity / Notes */}
      <Card>
        <CardContent>
          <Typography variant="h6" sx={{ fontWeight: 700, mb: 1 }}>Notes</Typography>
          <Grid container spacing={2}>
            <Grid item xs={12} md={9}>
              <TextField fullWidth size="small" label="Add internal note" placeholder="e.g., Panels delivered, QC scheduled tomorrow" />
            </Grid>
            <Grid item xs={12} md={3}>
              <Button variant="contained" fullWidth sx={{ backgroundColor: '#536DFE', '&:hover': { backgroundColor: '#3F51B5' } }}>Add Note</Button>
            </Grid>
          </Grid>
          <Divider sx={{ my: 2 }} />
          <Box>
            <Typography variant="caption" color="text.secondary">Recent Activity</Typography>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, mt: 0.5 }}>
              <Typography variant="body2">• QC checklist created by <b>Rahul Singh</b> (10:30 AM)</Typography>
              <Typography variant="body2">• Installation planned by <b>Admin</b> (Yesterday)</Typography>
              <Typography variant="body2">• Quotation approved by <b>Customer</b> (2 days ago)</Typography>
            </Box>
          </Box>
        </CardContent>
      </Card>
    </Box>
  );
};

export default ProgressTracking;


